/*
 * assets/hpo-pdp.js
 * ──────────────────────────────────────────────────────────────────────────
 * Alpine components for the HpO PDP (US-004). Faithful port of the hpo-web
 * (drinkhpo.com) PDP buy box + gallery, without touching the hashed Vite
 * bundle. Reuses the shared swipe helper registered by assets/hpo-home.js
 * (window.HpOHome.attachSwipe); falls back to a local copy if home JS is
 * absent on the PDP.
 *
 * Components:
 *   hpoGallery(count)        swipe + dot/arrow image gallery
 *   hpoPdp({ variants, ... }) pack selector ↔ Shopify variants, one-time vs
 *                            subscribe (native selling plans, graceful
 *                            degradation), per-can price, savings,
 *                            free-shipping nudge, submits id (+selling_plan).
 *
 * Money formatting defers to Shopify.formatMoney when present (matches the
 * rest of the theme); otherwise a USD fallback.
 * ──────────────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  function mod(n, m) {
    return ((n % m) + m) % m;
  }

  // Local swipe fallback (mirrors HpOHome.attachSwipe) so the PDP works even if
  // hpo-home.js wasn't loaded on this template.
  function localSwipe(el, opts) {
    if (!el) return function () {};
    var onLeft = opts.onLeft || function () {};
    var onRight = opts.onRight || function () {};
    var threshold = opts.threshold || 40;
    var startX = 0, startY = 0, active = false;
    function start(e) {
      if (e.touches.length !== 1) return;
      active = true; startX = e.touches[0].clientX; startY = e.touches[0].clientY;
    }
    function end(e) {
      if (!active) return; active = false;
      var t = e.changedTouches[0]; if (!t) return;
      var dx = t.clientX - startX, dy = t.clientY - startY;
      if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) onLeft(); else onRight();
    }
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchend', end, { passive: true });
    return function () {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchend', end);
    };
  }

  function swipe(el, opts) {
    if (window.HpOHome && window.HpOHome.attachSwipe) {
      return window.HpOHome.attachSwipe(el, opts);
    }
    return localSwipe(el, opts);
  }

  function fmtMoney(cents) {
    // Match the design exactly (no ISO currency code). Shopify.formatMoney would
    // render the shop's money_with_currency format which appends " USD", so we
    // format manually to keep prices as "$45.98".
    return '$' + (cents / 100).toFixed(2);
  }

  // Dispatch a namespaced analytics CustomEvent on document. analytics__events.liquid
  // listens for these so the homegrown hpo_* funnel fires from this redesigned
  // (US-004) buy box, which no longer uses the legacy option-input / Alpine
  // `current_variant_id` shape the analytics delegation originally targeted.
  // No PII is ever placed in detail.
  function hpoEmit(name, detail) {
    try {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
    } catch (e) {}
  }

  document.addEventListener('alpine:init', function () {
    var Alpine = window.Alpine;
    if (!Alpine) return;

    // ── Gallery ───────────────────────────────────────────────────────────
    Alpine.data('hpoGallery', function (count) {
      return {
        N: count || 1,
        idx: 0,
        initGallery: function () {
          var self = this;
          if (this.N <= 1) return;
          swipe(this.$refs.gallery, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
        },
        go: function (i) { this.idx = mod(i, this.N); },
        prev: function () { this.go(this.idx - 1); },
        next: function () { this.go(this.idx + 1); },
      };
    });

    // ── Buy box ───────────────────────────────────────────────────────────
    Alpine.data('hpoPdp', function (config) {
      var variants = (config && config.variants) || [];
      var sellingPlans = (config && config.sellingPlans) || [];
      var freeShipCans = (config && config.freeShipCans) || 16;
      var productMeta = (config && config.product) || {};

      // Default to the "Most popular" 16-pack when present, else first.
      var defaultIdx = 0;
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].cans === 16) { defaultIdx = i; break; }
      }

      return {
        variants: variants,
        sellingPlans: sellingPlans,
        freeShipCans: freeShipCans,
        product: productMeta,
        subscribeLabelText: (config && config.defaultDiscount) || 'Subscribe & save',
        packIdx: defaultIdx,
        // Subscribe-as-default (GATE A CRO): pre-select subscribe when the product
        // has native selling plans (PayWhirl); fall back to one-time when it has
        // none, so US-013 graceful degradation still holds and the ATC form only
        // posts a selling_plan when one actually exists.
        purchase: (sellingPlans.length > 0) ? 'subscribe' : 'once',
        planIdx: 0,

        // Fire variant_selected / pack_size_changed / subscription_toggled on
        // first hydration so analytics observes initial state transitions; the
        // $watch below catches every subsequent change. Bound from the section
        // via x-init="initAnalytics()".
        initAnalytics: function () {
          var self = this;
          var prevCans = this.currentVariant.cans;
          this.$watch('packIdx', function (idx) {
            var v = self.variants[idx];
            if (!v) return;
            hpoEmit('hpo:variant-selected', {
              product_id: String(self.product.id || ''),
              product_handle: self.product.handle || '',
              variant_id: String(v.id),
              cans: v.cans,
              price: (v.price || 0) / 100
            });
            if (prevCans && v.cans && prevCans !== v.cans) {
              hpoEmit('hpo:pack-changed', {
                product_id: String(self.product.id || ''),
                old_size: prevCans,
                new_size: v.cans,
                new_price: (v.price || 0) / 100
              });
            }
            prevCans = v.cans;
          });
          this.$watch('purchase', function (mode) {
            hpoEmit('hpo:subscription-toggled', {
              product_id: String(self.product.id || ''),
              new_state: mode === 'subscribe' ? 'subscribe' : 'one-time',
              selling_plan_id: (mode === 'subscribe' && self.activePlan) ? String(self.activePlan.id) : ''
            });
          });
        },

        // Called from the ATC form's @submit (alongside submitCartForm). The
        // form uses .prevent so we emit explicitly rather than relying on the
        // native submit-delegation fallback.
        emitAddToCart: function () {
          var v = this.currentVariant;
          var subscribing = this.purchase === 'subscribe' && !!this.activePlan;
          hpoEmit('hpo:add-to-cart', {
            product_id: String(this.product.id || ''),
            product_handle: this.product.handle || '',
            variant_id: String(v.id),
            quantity: 1,
            price: (v.price || 0) / 100,
            pack_size: v.cans,
            is_subscription: subscribing,
            selling_plan_id: subscribing ? String(this.activePlan.id) : ''
          });
        },

        get hasSubscription() { return this.sellingPlans.length > 0; },
        get currentVariant() { return this.variants[this.packIdx] || { id: 0, price: 0, available: false, cans: 1 }; },
        get activePlan() {
          if (!this.hasSubscription) return null;
          return this.sellingPlans[this.planIdx] || this.sellingPlans[0] || null;
        },
        get subscribeLabel() { return this.subscribeLabelText; },

        money: function (cents) { return fmtMoney(cents); },

        packLabel: function (v) {
          var m = (String(v.label).match(/\d+/) || [])[0];
          return (m ? m : v.label) + ' Cans';
        },
        perCan: function (v) {
          var cans = v.cans || (parseInt((String(v.label).match(/\d+/) || [])[0], 10) || 1);
          return this.money(Math.round(v.price / cans));
        },
        savings: function (v) {
          if (!v.compare || v.compare <= v.price) return null;
          var diff = Math.round((v.compare - v.price)); // cents
          if (diff <= 0) return null;
          return 'save ' + this.money(diff);
        },
        badgeFor: function (v) {
          if (v.cans === 16) return 'Most popular';
          if (v.cans === 24) return 'Best value';
          return '';
        },

        // Subscribe price = one-time price discounted. We display the discounted
        // total; the actual discount is applied server-side by the selling plan,
        // so this is presentational (matches hpo-web's 10% display).
        get subscribePriceLabel() {
          // Derive a percentage from the configured label ("…save 10%") if present.
          var pctMatch = String(this.subscribeLabelText).match(/(\d+)\s*%/);
          var pct = pctMatch ? parseInt(pctMatch[1], 10) / 100 : 0.1;
          return this.money(Math.round(this.currentVariant.price * (1 - pct)));
        },

        // Per-can subscribe price for the savings visualization (GATE A CRO).
        // Mirrors perCan() but applies the same presentational discount as
        // subscribePriceLabel; the real discount is applied server-side by the plan.
        subscribePerCan: function (v) {
          var pctMatch = String(this.subscribeLabelText).match(/(\d+)\s*%/);
          var pct = pctMatch ? parseInt(pctMatch[1], 10) / 100 : 0.1;
          var cans = v.cans || (parseInt((String(v.label).match(/\d+/) || [])[0], 10) || 1);
          return this.money(Math.round((v.price * (1 - pct)) / cans));
        },

        get freeShipMessage() {
          var cans = this.currentVariant.cans || 1;
          if (cans < this.freeShipCans) {
            // Show how many MORE cans are needed to cross the threshold, not the
            // threshold itself (e.g. 8 selected + 16 threshold → "Add 8 cans …").
            var needed = this.freeShipCans - cans;
            return 'Add ' + needed + ' cans to unlock free shipping';
          }
          return 'Free shipping included';
        },

        selectVariant: function (i) {
          if (!this.variants[i] || !this.variants[i].available) return;
          this.packIdx = i;
        },
      };
    });

    // ── Formula (5 ingredients) ─────────────────────────────────────────────
    // Desktop = click-driven focus over a 5-up grid. Mobile/tablet = infinite
    // swipe carousel (3 copies, silent snap-back to the middle copy) — same
    // pattern as hpo-web ProductFormula + the home Stats carousel.
    Alpine.data('hpoFormula', function (count) {
      return {
        N: count,
        active: 0, // desktop click focus
        position: count, // mobile carousel, start in middle copy
        animate: true,
        snapping: false,
        _snapTimer: null,
        get centered() { return mod(this.position, this.N); },
        initFormula: function () {
          var self = this;
          swipe(this.$refs.track, {
            onLeft: function () { self.next(); },
            onRight: function () { self.prev(); },
          });
        },
        prev: function () { this.animate = true; this.position--; this._scheduleSnap(); },
        next: function () { this.animate = true; this.position++; this._scheduleSnap(); },
        _scheduleSnap: function () {
          var self = this;
          if (this.snapping) return;
          if (this.position >= this.N && this.position < 2 * this.N) return;
          clearTimeout(this._snapTimer);
          this._snapTimer = setTimeout(function () {
            self.snapping = true;
            self.animate = false;
            self.position = self.N + mod(self.position, self.N);
            requestAnimationFrame(function () {
              requestAnimationFrame(function () {
                self.animate = true;
                self.snapping = false;
              });
            });
          }, 550);
        },
      };
    });

    // ── Videos + lightbox ───────────────────────────────────────────────────
    // Faithful port of hpo-web ProductVideos.tsx + VideoLightbox.tsx. Clips are
    // read from an inline JSON <script> (clipsElId). Grid of vertical posters →
    // full-screen lightbox with keyboard + arrow nav and background scroll-lock.
    Alpine.data('hpoVideos', function (clipsElId) {
      var clips = [];
      try {
        var el = document.getElementById(clipsElId);
        if (el) clips = JSON.parse(el.textContent);
      } catch (e) { clips = []; }
      return {
        clips: clips,
        lightboxIdx: null,
        get open() { return this.lightboxIdx !== null; },
        get current() { return this.lightboxIdx === null ? null : this.clips[this.lightboxIdx]; },
        initVideos: function () {
          var self = this;
          this._onKey = function (e) {
            if (!self.open) return;
            if (e.key === 'Escape') self.close();
            else if (e.key === 'ArrowLeft') self.lbPrev();
            else if (e.key === 'ArrowRight') self.lbNext();
          };
          window.addEventListener('keydown', this._onKey);
        },
        openLightbox: function (i) {
          this.lightboxIdx = mod(i, this.clips.length);
          document.body.style.overflow = 'hidden';
        },
        close: function () {
          this.lightboxIdx = null;
          document.body.style.overflow = '';
        },
        lbPrev: function () {
          if (this.lightboxIdx === null) return;
          this.lightboxIdx = mod(this.lightboxIdx - 1, this.clips.length);
        },
        lbNext: function () {
          if (this.lightboxIdx === null) return;
          this.lightboxIdx = mod(this.lightboxIdx + 1, this.clips.length);
        },
      };
    });

    // ── PDP Reviews (list → write form → thank-you) ─────────────────────────
    // Faithful port of hpo-web ProductReviews.tsx view-state machine. The form
    // is a placeholder submit (no endpoint yet) matching the source.
    Alpine.data('hpoPdpReviews', function () {
      return {
        view: 'list', // 'list' | 'form' | 'done'
        score: 0,
        hover: 0,
        get shown() { return this.hover || this.score; },
        submitReview: function (e) {
          if (e) e.preventDefault();
          this.view = 'done';
        },
      };
    });
  });
})();

// Sticky mobile add-to-cart (GATE A KILL-09): show #hpo-sticky-atc once the main
// ATC button has scrolled above the viewport. A window scroll/resize listener that
// re-queries the DOM each tick — robust to Shopify re-rendering the product section
// (which detaches one-shot observers and skips inline <script> tags).
(function () {
  function syncStickyAtc() {
    var m = document.getElementById('hpo-main-atc');
    var bar = document.getElementById('hpo-sticky-atc');
    if (!m || !bar) return;
    if (m.getBoundingClientRect().bottom <= 0) bar.classList.add('is-stuck');
    else bar.classList.remove('is-stuck');
  }
  window.addEventListener('scroll', syncStickyAtc, { passive: true });
  window.addEventListener('resize', syncStickyAtc, { passive: true });
  document.addEventListener('DOMContentLoaded', syncStickyAtc);
  document.addEventListener('shopify:section:load', syncStickyAtc);
  syncStickyAtc();
})();
